function bundleAddonCartAddUrl() {
  const u = window.routes?.cart_add_url || '/cart/add';
  return String(u).endsWith('.js') ? u : `${u}.js`;
}

function bundleAddonCartClearJsUrl() {
  const u = window.routes?.cart_url || '/cart';
  const base = String(u).replace(/\.js$/, '').replace(/\/$/, '') || '/cart';
  return `${base}/clear.js`;
}

function bundleAddonFormHasDynamicCheckout(form) {
  if (!form || typeof form.querySelector !== 'function') return false;
  return !!form.querySelector(
    'shopify-accelerated-checkout, shopify-accelerated-checkout-cart, .shopify-payment-button'
  );
}

/**
 * Prefer bundle attrs on <product-form> (card / complementary quick-add) so nested
 * forms inside a parent <product-info> do not inherit the wrong product handle.
 */
function bundleAddonResolveContext(fromEl) {
  if (!fromEl) return null;
  const form =
    fromEl.tagName && String(fromEl.tagName).toUpperCase() === 'PRODUCT-FORM'
      ? fromEl
      : typeof fromEl.closest === 'function'
        ? fromEl.closest('product-form')
        : null;
  if (form && (form.dataset.productHandle || form.dataset.bundleAddonHandle)) {
    return form;
  }
  if (typeof fromEl.closest === 'function') {
    return fromEl.closest('product-info') || fromEl;
  }
  return fromEl;
}

function bundleAddonComputeBundleState(fromEl) {
  const contextEl = bundleAddonResolveContext(fromEl);
  const cfg = window.bundleAddonAuto || {};
  const handle = (contextEl?.dataset.productHandle || '').trim().toLowerCase();
  const metafieldAddonHandle = (contextEl?.dataset.bundleAddonHandle || '').trim().toLowerCase();
  const metafieldAddonSource = (contextEl?.dataset.bundleAddonSource || '').trim().toLowerCase();
  const themeAddonHandle = String(cfg.addonProductHandle || '')
    .trim()
    .toLowerCase();
  const addonHandle = metafieldAddonHandle || themeAddonHandle;
  const parentHandles = bundleAddonParseParentHandles(cfg.parentHandlesRaw);
  const useMetafieldBundle = Boolean(metafieldAddonHandle);
  const qtyLock = useMetafieldBundle && metafieldAddonSource === 'product';
  const shouldAutoBundle =
    Boolean(addonHandle) &&
    (useMetafieldBundle || (handle && parentHandles.length > 0 && parentHandles.includes(handle)));
  return { shouldAutoBundle, handle, addonHandle, qtyLock, cfg };
}

function bundleAddonEventPathHasAcceleratedCheckout(evt) {
  const path = typeof evt.composedPath === 'function' ? evt.composedPath() : [];
  for (let i = 0; i < path.length; i++) {
    const n = path[i];
    const t = String(n && n.tagName ? n.tagName : '').toUpperCase();
    if (t === 'SHOPIFY-ACCELERATED-CHECKOUT' || t === 'SHOPIFY-ACCELERATED-CHECKOUT-CART') return true;
    if (n && n.classList && n.classList.contains('shopify-payment-button')) return true;
  }
  return false;
}

/** Buy now: checkout only this purchase, not mixed with existing cart lines. */
function bundleAddonClearCartForBuyNow() {
  return fetch(bundleAddonCartClearJsUrl(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
    body: '{}',
  }).then((res) => {
    if (!res.ok) throw new Error('CLEAR_CART');
    return res.json();
  });
}

function bundleAddonParseParentHandles(raw) {
  if (!raw || typeof raw !== 'string') return [];
  return raw
    .split(',')
    .map((h) => h.trim().toLowerCase())
    .filter(Boolean);
}

function bundleAddonFetchProductJson(handle) {
  return fetch(`/products/${encodeURIComponent(handle)}.js`).then((res) => {
    if (!res.ok) throw new Error('ADDON_FETCH');
    return res.json();
  });
}

const BUNDLE_ADDON_COLOR_OPTION_RE =
  /color|colour|颜色|顏色|色系|farbe|couleur|colou?r|款式|外观|样式/i;

function bundleAddonNormOptionName(v) {
  let s = String(v == null ? '' : v).trim().toLowerCase();
  try {
    if (typeof s.normalize === 'function') s = s.normalize('NFKC');
  } catch (e) {
    /* ignore */
  }
  return s;
}

function bundleAddonNormOptionValue(v) {
  let s = String(v == null ? '' : v).trim().toLowerCase();
  try {
    if (typeof s.normalize === 'function') s = s.normalize('NFKC');
  } catch (e) {
    /* ignore */
  }
  return s;
}

function bundleAddonColorOptionIndex(product) {
  const names = product?.options || [];
  for (let i = 0; i < names.length; i++) {
    if (BUNDLE_ADDON_COLOR_OPTION_RE.test(String(names[i] || ''))) return i;
  }
  return -1;
}

function bundleAddonOptionNamesAlign(parentName, addonName) {
  const a = bundleAddonNormOptionName(parentName);
  const b = bundleAddonNormOptionName(addonName);
  if (!a || !b) return false;
  if (a === b) return true;
  const colorish = (x) =>
    /^(colou?rs?|farbe|couleurs?|颜色|顏色|色系|款式|外观|样式)s?$/.test(x);
  if (colorish(a) && colorish(b)) return true;
  return false;
}

/** Same option title on parent & addon (e.g. both "Color"), possibly different positions. */
function bundleAddonSharedOptionPairs(parentProduct, addonProduct) {
  const po = parentProduct?.options || [];
  const ao = addonProduct?.options || [];
  const pairs = [];
  for (let i = 0; i < po.length; i++) {
    const pn = po[i];
    if (bundleAddonNormOptionName(pn) === '') continue;
    for (let j = 0; j < ao.length; j++) {
      if (bundleAddonOptionNamesAlign(pn, ao[j])) {
        pairs.push([i, j]);
        break;
      }
    }
  }
  return pairs;
}

function bundleAddonVariantMatchesSharedPairs(v, parentVariant, pairs) {
  let used = 0;
  for (let k = 0; k < pairs.length; k++) {
    const [pi, aj] = pairs[k];
    const pv = bundleAddonNormOptionValue(parentVariant[`option${pi + 1}`]);
    if (!pv) continue;
    used += 1;
    const av = bundleAddonNormOptionValue(v[`option${aj + 1}`]);
    if (pv !== av) return false;
  }
  return used > 0;
}

function bundleAddonFindBySharedOptionPairs(variants, parentVariant, pairs) {
  if (!pairs.length) return null;
  return variants.find((v) => bundleAddonVariantMatchesSharedPairs(v, parentVariant, pairs)) || null;
}

function bundleAddonPickVariant(addonProduct, parentProduct, parentVariant) {
  const variants = (addonProduct.variants || []).filter((v) => v.available);
  if (!variants.length) return null;

  const sharedPairs = bundleAddonSharedOptionPairs(parentProduct, addonProduct);
  const byShared = bundleAddonFindBySharedOptionPairs(variants, parentVariant, sharedPairs);
  if (byShared) return byShared;

  const parentIdx = bundleAddonColorOptionIndex(parentProduct);
  const addonIdx = bundleAddonColorOptionIndex(addonProduct);

  if (parentIdx >= 0 && addonIdx >= 0) {
    const parentKey = `option${parentIdx + 1}`;
    const addonKey = `option${addonIdx + 1}`;
    const wantRaw = parentVariant[parentKey];
    const want = bundleAddonNormOptionValue(wantRaw);
    if (want) {
      let found = variants.find((v) => v[addonKey] === wantRaw);
      if (found) return found;
      found = variants.find((v) => bundleAddonNormOptionValue(v[addonKey]) === want);
      if (found) return found;
      found = variants.find((v) =>
        [1, 2, 3].some((n) => bundleAddonNormOptionValue(v[`option${n}`]) === want)
      );
      if (found) return found;
    }
  }

  return variants[0];
}

function isShopifyAjaxCartError(body) {
  if (!body || typeof body !== 'object') return true;
  if (Array.isArray(body.errors) && body.errors.length > 0) return true;
  const s = body.status;
  if (s === 422) return true;
  if (typeof s === 'number' && s >= 400) return true;
  return false;
}

function getLineItemBundleRole(item) {
  const p = item?.properties;
  if (!p || typeof p !== 'object') return null;
  if (Array.isArray(p)) {
    const row = p.find((x) => String(x?.name ?? x?.key ?? '') === '_bundle_role');
    const v = row?.value ?? row?.last;
    return typeof v === 'string' ? v : null;
  }
  const r = p._bundle_role ?? p['_bundle_role'];
  return typeof r === 'string' ? r : null;
}

function getBundleParentLineItemKey(items) {
  if (!Array.isArray(items)) return null;
  for (let i = 0; i < items.length; i++) {
    if (getLineItemBundleRole(items[i]) === 'parent') return items[i].key;
  }
  return null;
}

function normalizeCartAddResponseForUi(body) {
  if (!body || typeof body !== 'object') return body;
  if (!Array.isArray(body.items) || !body.items.length) return body;
  const parentKey = getBundleParentLineItemKey(body.items);
  if (parentKey) return { ...body, key: parentKey };
  if (!body.key) {
    const last = body.items[body.items.length - 1];
    const first = body.items[0];
    return { ...body, key: last?.key || first?.key };
  }
  return body;
}

function bundleAddonCheckoutUrl() {
  const root =
    typeof window.Shopify !== 'undefined' &&
    window.Shopify.routes &&
    typeof window.Shopify.routes.root === 'string'
      ? window.Shopify.routes.root
      : null;
  if (root) {
    const r = root.endsWith('/') ? root : `${root}/`;
    return `${r}checkout`;
  }
  return '/checkout';
}

/** Dynamic checkout / Buy it now lives in these nodes (may be inside shadow DOM). */
function bundleAddonIsAcceleratedCheckoutSubmitter(el) {
  if (!el || typeof el.getRootNode !== 'function') return false;
  if (el.closest?.('shopify-accelerated-checkout, shopify-accelerated-checkout-cart')) return true;
  if (el.closest?.('.shopify-payment-button')) return true;
  const tag = String(el.tagName || '').toUpperCase();
  if (tag === 'SHOPIFY-ACCELERATED-CHECKOUT' || tag === 'SHOPIFY-ACCELERATED-CHECKOUT-CART') return true;

  const root = el.getRootNode();
  if (root && root !== document && root.host) {
    const host = root.host;
    const ht = String(host.tagName || '').toUpperCase();
    if (ht === 'SHOPIFY-ACCELERATED-CHECKOUT' || ht === 'SHOPIFY-ACCELERATED-CHECKOUT-CART') return true;
    if (host.closest?.('shopify-accelerated-checkout, shopify-accelerated-checkout-cart')) return true;
  }
  return false;
}

function bundleAddonRefreshCartAfterAdd(variantId) {
  return fetch('/cart.js')
    .then((r) => r.json())
    .then((cart) => {
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'product-form',
        variantId: variantId || null,
        cartData: cart,
      });

      const n = typeof cart.item_count === 'number' ? cart.item_count : 0;
      const cartLink = document.querySelector('#cart-icon-bubble');
      if (!cartLink) return cart;

      let bubble = cartLink.querySelector('.cart-count-bubble, .cl-cart-bubble');
      if (n > 0) {
        if (!bubble) {
          bubble = document.createElement('div');
          bubble.className = 'cl-cart-bubble cart-count-bubble';
          bubble.innerHTML = `<span class="cl-span-icon" aria-hidden="true">${n < 100 ? String(n) : ''}</span>`;
          cartLink.appendChild(bubble);
        } else {
          const vis = bubble.querySelector('span[aria-hidden="true"]');
          if (vis && n < 100) vis.textContent = String(n);
        }
        bubble.classList.remove('hidden');
      } else if (bubble) {
        bubble.remove();
      }
      return cart;
    });
}

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        const variantIdInput = this.form?.querySelector('[name=id]');
        if (this.dataset.bundleAddonOnly !== 'true' && variantIdInput) {
          variantIdInput.disabled = false;
        }
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this), { capture: true });
        this.addEventListener('click', this.onBundleWalletClickCapture.bind(this), true);
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton?.querySelector('span');

        if (this.submitButton && document.querySelector('cart-drawer')) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      /**
       * Accelerated checkout often bypasses a normal `submit` listener (e.g. form.submit() or internal handlers).
       * Capture clicks that originate from shopify-accelerated-checkout and run the same bundle + checkout flow.
       */
      onBundleWalletClickCapture(evt) {
        if (this.dataset.bundleAddonOnly === 'true' || !this.form) return;
        if (!bundleAddonEventPathHasAcceleratedCheckout(evt)) return;
        const { shouldAutoBundle } = bundleAddonComputeBundleState(this);
        if (!shouldAutoBundle) return;
        evt.preventDefault();
        evt.stopImmediatePropagation();
        this.runBundleBuyNowFlowInner(evt);
      }

      runBundleBuyNowFlowInner(evt) {
        if (!this.form) return;
        if (this._bundleBuyNowInProgress) return;
        this._bundleBuyNowInProgress = true;

        const formData = new FormData(this.form);
        const { shouldAutoBundle, handle, addonHandle, qtyLock } = bundleAddonComputeBundleState(this);
        if (!shouldAutoBundle) {
          this._bundleBuyNowInProgress = false;
          return;
        }

        const parentVariantId = formData.get('id');
        const quantity = Math.max(1, parseInt(formData.get('quantity') || '1', 10) || 1);

        const finishWithError = (message) => {
          this.error = true;
          this.handleErrorMessage(message);
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'product-form',
            productVariantId: formData.get('id'),
            errors: message,
            message,
          });
        };

        this.handleErrorMessage(false);
        if (this.submitButton) {
          this.submitButton.setAttribute('aria-disabled', true);
          this.submitButton.classList.add('loading');
        }
        this.querySelector('.loading__spinner')?.classList.remove('hidden');

        bundleAddonClearCartForBuyNow()
          .then(() =>
            this.requestBundleAdd(formData, parentVariantId, quantity, handle, addonHandle, qtyLock, false)
          )
          .then((response) => {
            if (isShopifyAjaxCartError(response)) {
              finishWithError(response.description || response.message || window.cartStrings.error);
              return;
            }
            const items = response?.items;
            const hasParent = Array.isArray(items) && items.some((it) => getLineItemBundleRole(it) === 'parent');
            const hasChild = Array.isArray(items) && items.some((it) => getLineItemBundleRole(it) === 'child');
            const twoLinesInResponse = Array.isArray(items) && items.length >= 2;
            if (!(hasParent && hasChild) && !twoLinesInResponse) {
              return fetch('/cart.js')
                .then((r) => r.json())
                .then((cart) => {
                  const lines = cart?.items || [];
                  const okByRole =
                    lines.length >= 2 &&
                    lines.some((it) => getLineItemBundleRole(it) === 'parent') &&
                    lines.some((it) => getLineItemBundleRole(it) === 'child');
                  const okByCount = lines.length >= 2;
                  if (!okByRole && !okByCount) {
                    finishWithError(
                      '立即购买未能同时加入搭售商品，请刷新后重试，或先使用「加入购物车」。'
                    );
                    return;
                  }
                  this.handleErrorMessage(false);
                  this.error = false;
                  window.location.assign(bundleAddonCheckoutUrl());
                })
                .catch(() => {
                  finishWithError(
                    '立即购买未能同时加入搭售商品，请刷新后重试，或先使用「加入购物车」。'
                  );
                });
            }
            this.handleErrorMessage(false);
            this.error = false;
            window.location.assign(bundleAddonCheckoutUrl());
          })
          .catch((e) => {
            console.error(e);
            finishWithError(
              '无法完成捆绑加购：请检查主商品元字段（配件 handle / 商品引用）或主题设置中的配件；配件需已发布到「网店」并有可售库存。'
            );
          })
          .finally(() => {
            this._bundleBuyNowInProgress = false;
            this.submitButton?.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton?.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner')?.classList.add('hidden');
            if (evt && typeof CartPerformance !== 'undefined') {
              CartPerformance.measureFromEvent('add:user-action', evt);
            }
          });
      }

      onSubmitHandler(evt) {
        if (this.dataset.bundleAddonOnly === 'true') return;

        const submitter = evt.submitter || document.activeElement;
        const { shouldAutoBundle, handle, addonHandle, qtyLock } = bundleAddonComputeBundleState(this);

        const isAddToCartSubmit =
          !!submitter &&
          (submitter.getAttribute?.('name') === 'add' ||
            submitter.id?.startsWith?.('ProductSubmitButton-') ||
            submitter.classList?.contains?.('product-form__submit'));

        const inAccelFromPath =
          typeof evt.composedPath === 'function' &&
          evt.composedPath().some((n) => {
            const t = String(n && n.tagName ? n.tagName : '').toUpperCase();
            return t === 'SHOPIFY-ACCELERATED-CHECKOUT' || t === 'SHOPIFY-ACCELERATED-CHECKOUT-CART';
          });

        const hasDynamicCheckoutInForm = bundleAddonFormHasDynamicCheckout(this.form);

        const isBundleBuyNow =
          shouldAutoBundle &&
          !isAddToCartSubmit &&
          (hasDynamicCheckoutInForm ||
            bundleAddonIsAcceleratedCheckoutSubmitter(submitter) ||
            bundleAddonIsAcceleratedCheckoutSubmitter(document.activeElement) ||
            inAccelFromPath);

        if (!isAddToCartSubmit && !isBundleBuyNow) return;

        evt.preventDefault();
        if (isBundleBuyNow) evt.stopPropagation();

        if (isBundleBuyNow) {
          this.runBundleBuyNowFlowInner(evt);
          return;
        }

        if (this.submitButton?.getAttribute?.('aria-disabled') === 'true') return;

        const formData = new FormData(this.form);

        this.handleErrorMessage();

        if (this.submitButton) {
          this.submitButton.setAttribute('aria-disabled', true);
          this.submitButton.classList.add('loading');
        }
        this.querySelector('.loading__spinner')?.classList.remove('hidden');

        const finishWithError = (message) => {
          this.error = true;
          this.handleErrorMessage(message);
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'product-form',
            productVariantId: formData.get('id'),
            errors: message,
            message,
          });
        };

        const runSuccessPath = (response) => {
          this.handleErrorMessage(false);
          this.error = false;

          if (!this.cart) {
            window.location = window.routes.cart_url;
            return;
          }

          const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            productVariantId: formData.get('id'),
            cartData: response,
          }).then(() => {
            CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
          });

          const quickAddModal = this.closest('quick-add-modal');
          const paintCartUi = () => {
            try {
              this.cart.renderContents(response);
            } catch (e) {
              console.warn('[product-form] cart renderContents', e);
              bundleAddonRefreshCartAfterAdd(formData.get('id'));
            }
          };

          if (quickAddModal) {
            document.body.addEventListener(
              'modalClosed',
              () => {
                setTimeout(() => {
                  CartPerformance.measure('add:paint-updated-sections', paintCartUi);
                });
              },
              { once: true }
            );
            quickAddModal.hide(true);
          } else {
            CartPerformance.measure('add:paint-updated-sections', paintCartUi);
          }
        };

        if (shouldAutoBundle) {
          const parentVariantId = formData.get('id');
          const quantity = Math.max(1, parseInt(formData.get('quantity') || '1', 10) || 1);

          this.requestBundleAdd(formData, parentVariantId, quantity, handle, addonHandle, qtyLock, true)
            .then((response) => {
              if (isShopifyAjaxCartError(response)) {
                finishWithError(response.description || response.message || window.cartStrings.error);
                return;
              }
              runSuccessPath(normalizeCartAddResponseForUi(response));
            })
            .catch((e) => {
              console.error(e);
              finishWithError(
                '无法完成捆绑加购：请检查主商品元字段（配件 handle / 商品引用）或主题设置中的配件；配件需已发布到「网店」并有可售库存。'
              );
            })
            .finally(() => {
              this.submitButton?.classList.remove('loading');
              if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
              if (!this.error) this.submitButton?.removeAttribute('aria-disabled');
              this.querySelector('.loading__spinner')?.classList.add('hidden');
              CartPerformance.measureFromEvent('add:user-action', evt);
            });
          return;
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (isShopifyAjaxCartError(response)) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              cartData: response,
            }).then(() => {
              CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
            });
            this.error = false;
            this.handleErrorMessage(false);
            const quickAddModal = this.closest('quick-add-modal');
            const paintCartUi = () => {
              try {
                this.cart.renderContents(normalizeCartAddResponseForUi(response));
              } catch (e) {
                console.warn('[product-form] cart renderContents', e);
                bundleAddonRefreshCartAfterAdd(formData.get('id'));
              }
            };
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure('add:paint-updated-sections', paintCartUi);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure('add:paint-updated-sections', paintCartUi);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton?.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton?.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner')?.classList.add('hidden');

            CartPerformance.measureFromEvent('add:user-action', evt);
          });
      }

      requestBundleAdd(
        formData,
        parentVariantId,
        quantity,
        parentHandle,
        addonHandle,
        qtyLock = false,
        includeSectionsForCartUi = true
      ) {
        return bundleAddonFetchProductJson(parentHandle)
          .then((parentProduct) => {
            const parentVariant = (parentProduct.variants || []).find(
              (v) => String(v.id) === String(parentVariantId)
            );
            if (!parentVariant) throw new Error('PARENT_VARIANT');
            return bundleAddonFetchProductJson(addonHandle).then((addonProduct) => ({
              parentProduct,
              parentVariant,
              addonProduct,
            }));
          })
          .then(({ parentVariant, addonProduct, parentProduct }) => {
            const addonVariant = bundleAddonPickVariant(addonProduct, parentProduct, parentVariant);
            if (!addonVariant) throw new Error('ADDON_VARIANT');

            const groupId =
              typeof crypto !== 'undefined' && crypto.randomUUID
                ? crypto.randomUUID()
                : `bundle-${Date.now()}`;

            const items = [
              {
                id: Number(parentVariantId),
                quantity,
                properties: qtyLock
                  ? { _bundle_group: groupId, _bundle_role: 'parent' }
                  : { _bundle_group: groupId, _bundle_role: 'parent' },
              },
              {
                id: addonVariant.id,
                quantity,
                properties: qtyLock
                  ? { _bundle_group: groupId, _bundle_role: 'child', _bundle_qty_lock: '1' }
                  : { _bundle_group: groupId, _bundle_role: 'child' },
              },
            ];

            const sellingPlan = formData.get('selling_plan');
            if (sellingPlan) items[0].selling_plan = Number(sellingPlan) || sellingPlan;

            const body = { items };
            if (
              includeSectionsForCartUi &&
              this.cart &&
              typeof this.cart.getSectionsToRender === 'function'
            ) {
              const sectionIds = this.cart.getSectionsToRender().map((section) => section.id).join(',');
              if (sectionIds) {
                body.sections = sectionIds;
                body.sections_url = window.location.pathname;
              }
            }

            return fetch(bundleAddonCartAddUrl(), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify(body),
            }).then((res) => res.json());
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (this.dataset.bundleAddonOnly === 'true') {
          this.submitButton.setAttribute('disabled', 'disabled');
          this.submitButtonText.textContent = '随主商品加购';
          return;
        }
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
